import { Sidebar } from './Sidebar';
import { useSidebarState } from '../../hooks/useSidebarState';

const RefreshIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

interface LayoutProps {
  children: React.ReactNode;
}

/**
 * Standard layout with sidebar for all pages
 */
export function Layout({ children }: LayoutProps) {
  const { isCollapsed, toggle: toggleSidebar, collapse: collapseSidebar } = useSidebarState();

  const handleMainClick = () => {
    if (!isCollapsed) {
      collapseSidebar();
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-dark-base transition-colors">
      <Sidebar
        isCollapsed={isCollapsed}
        onToggle={toggleSidebar}
      />
      <main className="flex-1 overflow-auto" onClick={handleMainClick}>
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
}

/**
 * Dashboard layout with sidebar - includes active section handling
 * Used only on the main dashboard page
 */
interface DashboardLayoutProps {
  sidebar: React.ReactNode;
  children: React.ReactNode;
}

export function DashboardLayout({ sidebar, children }: DashboardLayoutProps) {
  const { isCollapsed, collapse: collapseSidebar } = useSidebarState();

  const handleMainClick = () => {
    if (!isCollapsed) {
      collapseSidebar();
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-dark-base transition-colors">
      {sidebar}
      <main className="flex-1 overflow-auto" onClick={handleMainClick}>
        {children}
      </main>
    </div>
  );
}

// Page header component for reuse
interface PageHeaderProps {
  title: string;
  subtitle?: string;
  onRefresh?: () => void;
  refreshCountdown?: number; // seconds until next auto-refresh
  actions?: React.ReactNode;
}

export function PageHeader({ title, subtitle, onRefresh, refreshCountdown, actions }: PageHeaderProps) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-4 mb-1">
        <h2 className="text-2xl font-medium text-gray-800 dark:text-gray-100">
          {title}
        </h2>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-purple-500 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-md transition-colors"
          >
            <RefreshIcon />
            Refresh Queue
            {refreshCountdown !== undefined && (
              <span className="text-xs text-gray-400 dark:text-gray-500 font-normal ml-1">
                ({refreshCountdown}s)
              </span>
            )}
          </button>
        )}
        {actions}
      </div>
      {subtitle && (
        <p className="text-gray-500 dark:text-gray-400">{subtitle}</p>
      )}
    </div>
  );
}
